import React, { createContext, useState, ReactNode, useContext } from "react";
import {
  ProjectDetailResponse,
  ProjectResponse,
  createProjectUserPayloadModel,
  exportTeamOptionResponse,
  updateProjectActivityPayload,
} from "../model/projectModel";
import useProjectApi from "../api/project";
import { getUserIdTokenForIonTime } from "../helper/api";

interface ManageProjectContextType {
  projectFormType: string;
  setProjectFormType: (item: string) => void;
  project: ProjectResponse[] | null;
  projectId: number | null;
  setProjectID: (projectId: number) => void;
  setProject: (item: ProjectResponse[] | null) => void;
  projectDetail: ProjectDetailResponse | null;
  setProjectDetail: (item: ProjectDetailResponse | null) => void;
  memberOptionDetail: exportTeamOptionResponse | null;
  setsMemberOptionDetail: (item: exportTeamOptionResponse | null) => void;
  // getProjects: () => void;
  getProjects: (status: number) => void;
  getMemberOptionsDetails: (item: number) => void;
  addProjectMember: (item: createProjectUserPayloadModel) => void;
  getProjectDetails: (item: number) => void;
  updateProjectActivity: (item: updateProjectActivityPayload[]) => void;
  addProjectActivity: (item: updateProjectActivityPayload[]) => void;
  deleteProjectActivity: (projects_id: number, version_id: number) => Promise<void>;
  getProjectDetail: (item: number) => void;
}

const defaultManageProjectContext: ManageProjectContextType = {
  projectFormType: "update",
  setProjectFormType: () => {},
  projectDetail: null,
  projectId: null,
  setProjectID: () => {},
  project: null,
  setProject: () => {},
  setProjectDetail: () => {},
  memberOptionDetail: null,
  setsMemberOptionDetail: () => {},
  getProjects: () => {},
  getMemberOptionsDetails: () => {},
  addProjectMember: () => {},
  getProjectDetails: () => {},
  updateProjectActivity: () => {},
  addProjectActivity: async () => {},
  deleteProjectActivity: async () => {},
  getProjectDetail: () => {},
};

const ManageProjectContext = createContext<ManageProjectContextType>(
  defaultManageProjectContext,
);

export const ManageProjectProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const getUserId = getUserIdTokenForIonTime();
  const [projectFormType, setProjectFormType] = useState<string>("update");
  const [projectId, setProjectID] = useState<number | null>(null);
  const [project, setProject] = useState<ProjectResponse[] | null>(null);
  const [projectDetail, setProjectDetail] =
    useState<ProjectDetailResponse | null>(null);
  const [memberOptionDetail, setsMemberOptionDetail] =
    useState<exportTeamOptionResponse | null>(null);
  const {
    fetchProject,
    fetchTeamOptionDetail,
    fetchProjectDetail,
    // createProject,
    createProjectUser,
    updateProjectActivityApi,
    addProjectActivityApi,
    deleteProjectActivityApi,
    fetchGetPromiseProjectDetail,
  } = useProjectApi();

  // const getProjects = React.useCallback(async () => {
  //   try {
  //     const responseData = await fetchProject(Number(getUserId));
  //     setProject(responseData);
  //   } catch (error) {
  //     // setError(error.message);
  //   }
  // }, [getUserId, fetchProject, setProject]);
  const getProjects = React.useCallback(
    async (status: number) => {
      try {
        const responseData = await fetchProject(Number(getUserId), status);
        setProject(responseData);
      } catch (error) {
        // setError(error.message);
      }
    },
    [getUserId, fetchProject, setProject],
  );
  const getMemberOptionsDetails = React.useCallback(
    async (projectID: number) => {
      try {
        const response = await fetchTeamOptionDetail(projectID);
        setsMemberOptionDetail(response);
        return response;
      } catch (error) {
        // setError(error.message);
      }
    },
    [fetchTeamOptionDetail],
  );

  const getProjectDetails = React.useCallback(
    async (projectID: number) => {
      try {
        const response = await fetchGetPromiseProjectDetail(projectID);
        setProjectDetail(response?.projectDetails ?? null);
        setsMemberOptionDetail(response?.OptionDetail ?? null);
      } catch (error) {
        // setError(error.message);
      }
    },
    [setProjectDetail, setsMemberOptionDetail],
  );

  const getProjectDetail = React.useCallback(
    async (projectID: number) => {
      try {
        const projectDetail = await fetchProjectDetail(projectID);
        setProjectDetail(projectDetail);
      } catch (error) {
        // setError(error.message);
      }
    },
    [fetchProjectDetail],
  );

  // const addProject = useCallback(
  //   async (user: createUpdateProjectPayloadModel) => {
  //     // setLoading(true, "Create Project");
  //     try {
  //       const newUser = await createProject(user);

  //     } catch (error) {
  //       //   setError(error.message);
  //     } finally {
  //       // setLoading(false);
  //     }
  //   },
  //   [createProject],
  // );

  const addProjectMember = React.useCallback(
    async (member: createProjectUserPayloadModel) => {
      // setLoading(true, "Save data");
      try {
        const newUser = await createProjectUser(member);
        getProjectDetails(member.projects_id);
        return newUser;
      } catch (error) {
        //   setError(error.message);
      }
    },
    [createProjectUser, getProjectDetails],
  );

  const updateActivitiesIsEnabled = (
    updates: updateProjectActivityPayload[],
  ) => {
    if (!projectDetail) return;
  
    // Create a new all_activities array with the updates applied
    const updatedActivities = projectDetail.activities.map((activity) => {
      const update = updates.find((u) => u.version_id === activity.version_id);
      if (update) {
        return { ...activity, is_enabled: update.is_enabled };
      }
      return activity;
    });
  
    // Set the new projectDetail state with the updated all_activities
    setProjectDetail({
      ...projectDetail,
      activities: updatedActivities,
    });
  };

  const updateProjectActivity = React.useCallback(
    async (activity: updateProjectActivityPayload[]) => {
      try {
        const activityupdate = await updateProjectActivityApi(activity);
        if (activityupdate) {
          updateActivitiesIsEnabled(activity);
        }
        return activityupdate;
      } catch (error) {
        //   setError(error.message);
      }
    },
    [updateProjectActivityApi],
  );

  const addProjectActivity = React.useCallback(
    async (activity: updateProjectActivityPayload[]) => {
      try {
        const result = await addProjectActivityApi(activity);
        if (result && activity.length > 0) {
          await getProjectDetails(activity[0].projects_id);
        }
      } catch (error) {
        // setError(error.message);
      }
    },
    [addProjectActivityApi, getProjectDetails],
  );

  const deleteProjectActivity = React.useCallback(
    async (projects_id: number, version_id: number) => {
      try {
        await deleteProjectActivityApi({ projects_id, version_id });
        setProjectDetail((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            activities: prev.activities.filter((a) => a.version_id !== version_id),
          };
        });
      } catch (error) {
        // setError(error.message);
      }
    },
    [deleteProjectActivityApi, setProjectDetail],
  );

  return (
    <ManageProjectContext.Provider
      value={{
        project,
        setProject,
        projectDetail,
        setProjectDetail,
        memberOptionDetail,
        setsMemberOptionDetail,
        projectId,
        setProjectID,
        projectFormType,
        setProjectFormType,
        getProjects,
        getMemberOptionsDetails,
        addProjectMember,
        getProjectDetails,
        updateProjectActivity,
        addProjectActivity,
        deleteProjectActivity,
        getProjectDetail,
      }}
    >
      {children}
    </ManageProjectContext.Provider>
  );
};

export const useManageProjectContext = () => useContext(ManageProjectContext);

export { ManageProjectContext };
